import React, { useState } from 'react';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import { ThemeProvider } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
//import axios from 'axios';
import { useTranslation } from 'react-i18next';

import api from '../api/api';

const handleGoogleLogin = async () => {
  try {
    const response = await api.get(`${process.env.REACT_APP_API_BASE_URL}/api/auth/google/url`);
    window.location.href = response.data.url;
  } catch (error) {
    console.error('Error initiating Google login:', error);
    // Handle error appropriately
  }
};

export default function SignIn({ onCancel, onSignUpClick, onLoginSuccess, onForgotPasswordClick }) {
  const theme = useTheme();
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [error, setError] = useState('');
  const { t } = useTranslation();

  const handleSubmit = async (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);

    try {
      const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/api/auth/login`, {
        email: data.get('email'),
        password: data.get('password'),
      });

      if (response.data.success) {
        // Clear any existing token first
        localStorage.removeItem('jwtToken');

        // Store the JWT token in localStorage
        localStorage.setItem('jwtToken', response.data.token);

        // Update axios default headers for future requests
        api.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;

        // Proceed with login success actions
        onLoginSuccess();

      } else {
        setErrorMessage(response.data.message);
        setShowErrorMessage(true);
      }

    } catch (error) {
      const errorMsg = error.response?.data?.message || 'An unexpected error occurred';
      setErrorMessage(errorMsg);
      setShowErrorMessage(true);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
            <LockOutlinedIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            {t('auth.signIn')}
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
            <Button
              fullWidth
              variant="contained"
              color="secondary"
              onClick={handleGoogleLogin}
              sx={{ mb: 2 }}
            >
              Sign in with Google
            </Button>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label={t('auth.email')}
              name="email"
              autoComplete="email"
              autoFocus
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label={t('auth.password')}
              type="password"
              id="password"
              autoComplete="current-password"
            />
            <Button
              data-testid="sign-in-button"
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
            >
              {t('auth.signIn')}
            </Button>
            <Button
              fullWidth
              variant="contained"
              color="secondary"
              onClick={onCancel}
              sx={{ mb: 2 }}
            >
              {t('cancel')}
            </Button>
            <Grid container>
              <Grid item xs>
                <Link href="#" variant="body2" onClick={onForgotPasswordClick}>
                  {t('auth.forgotPassword')}
                </Link>
              </Grid>
              <Grid item>
                <Link href="#" variant="body2" onClick={onSignUpClick}>
                  {t('auth.dontHaveAccount')}
                </Link>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Container>
      <Snackbar
        ContentProps={{
          style: {
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
          },
        }}
        autoHideDuration={2000}
        open={showErrorMessage}
        onClose={() => setShowErrorMessage(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowErrorMessage(false)} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}
